import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, FieldWrapper, Switch, TooltipIcon, Wrapper } from 'components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Site, updateGracePeriodPolicyRequest } from 'store/Site';
import { IStore } from 'store/types';
import { GracePeriodTimeWorked, IDaySegment, ISite } from 'types';
import { ActionSection, Title } from '../../../styles';
import { NumberInput } from '../../../TipCalculator/shared/styles';

interface IGracePeriodFormProps {
  site: ISite;
  daySegments?: IDaySegment[];
  onClose(): void;
}

// Allow whole numbers up to 3 digits
const minutesPattern = /^\d{1,3}$/i;

const validateMinutes = (value: string) => value === '' || minutesPattern.test(value);

const today = dayjs().format('YYYY-MM-DD');

const getDaySegmentDuration = (segment: IDaySegment) => {
  const start = dayjs(`${today} ${segment.start}`);
  let end = dayjs(`${today} ${segment.end}`);

  if (end.isBefore(start)) end = end.add(1, 'day');
  return end.diff(start, 'minute');
};

export const GracePeriodForm: React.FC<IGracePeriodFormProps> = ({ site, daySegments, onClose }) => {
  const dispatch = useDispatch();
  const isUpdateLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.UPDATE_GRACE_PERIOD_POLICY_REQUEST));
  const { gracePeriodPolicy } = site?.settings || {};

  const shortestDaySegmentDuration = useMemo(
    () =>
      (daySegments || []).reduce((shortest, current) => {
        const currentDuration = getDaySegmentDuration(current);
        return !shortest || currentDuration < shortest ? currentDuration : shortest;
      }, 0),
    [daySegments]
  );
  const halfDuration = Math.floor(shortestDaySegmentDuration / 2);

  const validationSchema = Yup.object().shape({
    duration: Yup.number()
      .required('Required')
      .test('max', `Max duration is half of the shortest day segment (${halfDuration} min${halfDuration !== 1 ? 's' : ''})`, (duration) => !shortestDaySegmentDuration || duration <= halfDuration),
    timeWorked: Yup.boolean(),
  });

  const initialValues = {
    duration: gracePeriodPolicy?.segmentStart || 0,
    timeWorked: gracePeriodPolicy?.timeWorked === GracePeriodTimeWorked.INCLUDE,
  };

  const handleSubmit = (values: { duration: number; timeWorked: boolean }) => {
    const gracePeriod = {
      segmentStart: values.duration,
      segmentEnd: values.duration,
      timeWorked: values.timeWorked ? GracePeriodTimeWorked.INCLUDE : GracePeriodTimeWorked.EXCLUDE,
    };
    dispatch(updateGracePeriodPolicyRequest(site.id, gracePeriod, onClose));
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ setFieldValue, dirty, isValid }) => (
        <Form data-cy="grace-period-form">
          <Field name="duration">
            {({ field, meta }: FieldProps) => (
              <FieldWrapper label="Grace Period Duration" subLabel="(Minutes)" data-cy="duration" hasError={Boolean(meta.error)} message={meta.error || ''}>
                <NumberInput
                  {...field}
                  inputMode="decimal"
                  onChange={(e) => {
                    if (!validateMinutes(e.target.value)) return;
                    setFieldValue('duration', Number(e.target.value) || 0);
                  }}
                  $error={Boolean(meta.error)}
                  disabled={isUpdateLoading}
                  id="duration-input"
                />
              </FieldWrapper>
            )}
          </Field>
          <Field name="timeWorked">
            {({ field }: FieldProps) => (
              <Wrapper direction="row" alignItems="center" noWrap data-cy="time-worked-switch">
                <Switch checked={field.value} onChange={() => setFieldValue('timeWorked', !field.value)} disabled={isUpdateLoading} />
                <Title fontWeight="regular">Count time worked during grace period</Title>
                <TooltipIcon id="time-worked-tooltip" place="top" iconSize={20} iconMarginLeft={0}>
                  If enabled, time worked during the grace period
                  <br />
                  will be included in the employee's segment time.
                </TooltipIcon>
              </Wrapper>
            )}
          </Field>

          <ActionSection>
            <Button label="Cancel" data-cy="cancel-button" appearance="white" size="small" onClick={onClose} disabled={isUpdateLoading} />
            <Button type="submit" label="Save" data-cy="save-grace-period-button" size="small" isProcessing={isUpdateLoading} disabled={isUpdateLoading || !dirty || !isValid} />
          </ActionSection>
        </Form>
      )}
    </Formik>
  );
};
